import type { NavigationNode } from '../model/types';

/**
 * Упорядочивает верхнеуровневые узлы по пользовательскому порядку.
 *
 * Узлы, которых в пользовательском порядке нет (новые или не переставлявшиеся),
 * идут после переставленных — в системном порядке, поэтому появление новой группы
 * не требует повторной настройки.
 */
export const applyTopLevelOrder = (nodes: NavigationNode[], order?: string[]): NavigationNode[] => {
  if (!order?.length) return nodes;

  const rankById = new Map(order.map((id, index) => [id, index]));
  const ordered = nodes
    .filter((node) => rankById.has(node.id))
    .sort((a, b) => rankById.get(a.id)! - rankById.get(b.id)!);
  const rest = nodes.filter((node) => !rankById.has(node.id));

  return [...ordered, ...rest];
};

/**
 * Встраивает новый порядок узлов открытого диалога в сохранённый пользовательский порядок.
 *
 * Записи узлов, которых в открытом диалоге нет, остаются на своих местах: перестановка
 * заполняет только «слоты» присутствующих узлов, поэтому настройка, снятая на одном диалоге,
 * не ломается перестановкой в другом. Узлы, которых в сохранённом порядке ещё не было,
 * дописываются в конец.
 */
export const mergeTopLevelOrder = (savedOrder: string[] | undefined, dialogOrder: string[]): string[] => {
  if (!savedOrder?.length) return dialogOrder;

  const dialogNodeIds = new Set(dialogOrder);
  const result: string[] = [];
  let nextIndex = 0;

  for (const nodeId of savedOrder) {
    if (dialogNodeIds.has(nodeId)) {
      result.push(dialogOrder[nextIndex]);
      nextIndex += 1;
    } else {
      result.push(nodeId);
    }
  }
  result.push(...dialogOrder.slice(nextIndex));

  return result;
};
